import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, MessageCircle, ThumbsUp } from "lucide-react";

const formatCount = (value) =>
  new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(
    Number(value) || 0
  );

const VideoCard = ({ video, index }) => {
  const [playVideo, setPlayVideo] = useState(false);

  return (
    <div className="group relative overflow-hidden rounded-[28px] border border-black/10 bg-white backdrop-blur-xl shadow-[0_20px_80px_rgba(0,0,0,0.12)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_120px_rgba(255,120,0,0.25)]">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

      <div className="relative aspect-video overflow-hidden bg-gray-100">
        {!playVideo ? (
          <button
            onClick={() => setPlayVideo(true)}
            className="relative w-full h-full"
          >
            <img
              src={video.thumbnail}
              alt={video.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-2xl">
                <svg
                  className="w-8 h-8 text-red-600 ml-1"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </button>
        ) : (
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
            title={video.title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        )}
      </div>

      <div className="relative p-5">
        {/* <span className="text-orange-400 text-[10px] tracking-[0.25em] uppercase font-bold">
          Video #{index + 1}
        </span> */}
        <h3 className="text-gray-900 text-lg font-bold mt-3 leading-snug line-clamp-2 group-hover:text-orange-600 transition-colors duration-300">
          {video.title}
        </h3>
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs font-semibold text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Eye size={14} aria-hidden="true" />
            {formatCount(video.viewCount)} views
          </span>
          <span className="inline-flex items-center gap-1">
            <ThumbsUp size={14} aria-hidden="true" />
            {formatCount(video.likeCount)} likes
          </span>
          <span className="inline-flex items-center gap-1">
            <MessageCircle size={14} aria-hidden="true" />
            {formatCount(video.commentCount)} comments
          </span>
        </div>
      </div>
    </div>
  );
};

const GalleryVideos = () => {
  const { t } = useTranslation();

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [hasMorePages, setHasMorePages] = useState(true);

  // Search & Filter Configurations
  const [searchQuery, setSearchQuery] = useState("");
  const [debounceQuery, setDebounceQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("latest");
  const [contentType, setContentType] = useState("all");

  const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
  const channelId = import.meta.env.VITE_YOUTUBE_CHANNEL_ID;

  // Debounce Engine: Updates 'debounceQuery' 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounceQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Core API Fetch Execution Engine
  const fetchVideosFromAPI = async (isLoadMore = false, targetSearch = "") => {
    if (!isLoadMore) setLoading(true);
    else setLoadingMore(true);

    try {
      let url = `https://www.googleapis.com/youtube/v3/search?key=${apiKey}&channelId=${channelId}&part=snippet,id&type=video&order=date&maxResults=50`;

      if (targetSearch.trim() !== "") {
        url += `&q=${encodeURIComponent(targetSearch)}`;
      }

      if (isLoadMore && nextPageToken) {
        url += `&pageToken=${nextPageToken}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      const videoItems = data.items?.filter((item) => item.id.videoId) || [];
      const videoIds = videoItems.map((item) => item.id.videoId).join(",");
      const statisticsResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?key=${apiKey}&part=statistics&id=${videoIds}`
      );
      const statisticsData = await statisticsResponse.json();
      const statisticsById = new Map(
        (statisticsData.items || []).map((item) => [item.id, item.statistics || {}])
      );

      const formattedVideos = videoItems
        .map((item) => {
          const title = item.snippet.title || "";
          const isShort = title.toLowerCase().includes("#shorts") ||
            (item.snippet.description && item.snippet.description.toLowerCase().includes("#shorts"));
          const statistics = statisticsById.get(item.id.videoId) || {};

          return {
            id: item.id.videoId,
            title: title,
            isShort: isShort,
            publishedAt: item.snippet.publishedAt,
            viewCount: statistics.viewCount || 0,
            likeCount: statistics.likeCount || 0,
            commentCount: statistics.commentCount || 0,
            thumbnail:
              item.snippet.thumbnails.high?.url ||
              item.snippet.thumbnails.medium?.url ||
              item.snippet.thumbnails.default?.url,
          };
        });

      if (isLoadMore) {
        setVideos((prev) => [...prev, ...formattedVideos]);
      } else {
        setVideos(formattedVideos);
        setVisibleCount(20);
      }

      setNextPageToken(data.nextPageToken || null);
      setHasMorePages(!!data.nextPageToken);
    } catch (error) {
      console.log("YouTube API Sync Error:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Re-run global search when the debounced string finishes updating
  useEffect(() => {
    fetchVideosFromAPI(false, debounceQuery);
  }, [debounceQuery, apiKey, channelId]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebounceQuery("");
  };

  const handleLoadMore = () => {
    const nextVisibleCount = visibleCount + 20;

    if (nextVisibleCount <= processedVideos.length || !hasMorePages) {
      setVisibleCount(nextVisibleCount);
    } else {
      fetchVideosFromAPI(true, debounceQuery);
    }
  };

  // Live client filter matching over current pools
  const processedVideos = useMemo(() => {
    let result = [...videos];

    // Immediate instant search feedback for current pool while background API resolves
    if (searchQuery.trim() !== "") {
      result = result.filter((video) =>
        video.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (contentType === "video") {
      result = result.filter((video) => !video.isShort);
    } else if (contentType === "shorts") {
      result = result.filter((video) => video.isShort);
    }

    if (sortOrder === "latest") {
      result.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
    } else {
      result.sort((a, b) => new Date(a.publishedAt) - new Date(b.publishedAt));
    }

    return result;
  }, [videos, searchQuery, sortOrder, contentType]);

  return (
    <>
 <Helmet>
        <title>Harisharan Devgan Videos | Agriculture &amp; Public Affairs</title>
        <meta
          name="description"
          content="Watch videos featuring Harisharan Devgan, including news coverage, media interviews, public appearances and his views on agriculture, farmer welfare, politics and national affairs."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://harisharandevgan.com/Photos/" />

        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Harisharan Devgan Videos | Agriculture &amp; Public Affairs" />
        <meta
          property="og:description"
          content="Watch videos featuring Harisharan Devgan, including news coverage, media interviews, public appearances and his views on agriculture, farmer welfare, politics and national affairs."
        />
        <meta property="og:url" content="https://harisharandevgan.com/Photos/" />
        <meta property="og:site_name" content="Harisharan Devgan" />
        <meta property="og:updated_time" content="2026-02-01T11:53:28+05:30" />
        <meta
          property="og:image"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta
          property="og:image:secure_url"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="576" />
        <meta property="og:image:alt" content="Connect" />
        <meta property="og:image:type" content="image/webp" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Harisharan Devgan Videos | Agriculture &amp; Public Affairs" />
        <meta
          name="twitter:description"
          content="Watch videos featuring Harisharan Devgan, including news coverage, media interviews, public appearances and his views on agriculture, farmer welfare, politics and national affairs."
        />
        <meta
          name="twitter:image"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta name="twitter:label1" content="Time to read" />
        <meta name="twitter:data1" content="Less than a minute" />

        {/* Favicons / Icons */}
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
        <link rel="manifest" href="/favicon/site.webmanifest" />
        <link rel="shortcut icon" href="/favicon/favicon.ico" />
      </Helmet>

      {/* HERO */}
      <section className="relative min-h-[400px] flex items-center mt-[98px] overflow-hidden">
        <img
          src="/images/gallery/gallery-banner-new.webp"
          alt="Gallery Page Banner"

          /* Performance */
          loading="eager"
          fetchpriority="high"
          decoding="async"

          /* CLS fix */
          width="1920"
          height="600"

          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="relative max-w-7xl mx-auto w-full px-6 grid grid-cols-1 md:grid-cols-2 items-center gap-8">
          {(() => {
            const { t } = useTranslation();

            return (
              <>
                <div>
                  <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3">
                    {t("videos.title")}
                  </h1>
                  <p className="text-lg text-gray-300">
                    {t("videos.subtitle")}
                  </p>
                </div>

                <div className="md:text-right">
                  <h2 className="text-lg font-semibold text-white">
                    <Link
                      to="/"
                      className="text-gray-300 hover:text-orange-500 transition"
                    >
                      {t("common.home")}
                    </Link>
                    <span className="mx-2 text-gray-400">/</span>
                    <span className="text-orange-500">
                      {t("videos.title")}
                    </span>
                  </h2>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* VIDEOS LISTING SECTION */}
      <section className="relative bg-white py-16 overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-orange-500/10 blur-[180px] rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-yellow-500/10 blur-[180px] rounded-full"></div>

        <div className="relative w-full px-2 sm:px-4">

          {/* CONTROL STRIP */}
          <div className="max-w-7xl mx-auto mb-10 px-2 flex flex-col lg:flex-row gap-6 items-center justify-between">

            {/* Real-time Debounced Search Box */}
            <div className="relative w-full lg:max-w-md">
              {/* <input
                type="text"
                placeholder="Search videos (As you type...)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50 transition-colors duration-300"
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-sm"
                >
                  Clear
                </button>
              )} */}
            </div>

            {/* Quick Content Filters & Date Sort Elements */}
            <div className="flex flex-wrap items-center gap-6 justify-end w-full lg:w-auto">

              {/* Layout Component Filters */}
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm font-medium mr-1">Type:</span>
                <button
                  onClick={() => setContentType("all")}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all duration-300 ${contentType === "all"
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-black/5 text-gray-600 border-black/10 hover:text-gray-900"
                    }`}
                >
                  All
                </button>
                <button
                  onClick={() => setContentType("video")}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all duration-300 ${contentType === "video"
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-black/5 text-gray-600 border-black/10 hover:text-gray-900"
                    }`}
                >
                  Videos
                </button>
                <button
                  onClick={() => setContentType("shorts")}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all duration-300 ${contentType === "shorts"
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-black/5 text-gray-600 border-black/10 hover:text-gray-900"
                    }`}
                >
                  Shorts
                </button>
              </div>

              {/* Chronological State Toggles */}
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm font-medium mr-1">Sort:</span>
                <button
                  onClick={() => setSortOrder("latest")}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all duration-300 ${sortOrder === "latest"
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-black/5 text-gray-600 border-black/10 hover:text-gray-900"
                    }`}
                >
                  Latest
                </button>
                <button
                  onClick={() => setSortOrder("oldest")}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all duration-300 ${sortOrder === "oldest"
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-black/5 text-gray-600 border-black/10 hover:text-gray-900"
                    }`}
                >
                  Oldest
                </button>
              </div>

            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-32">
              <div className="w-14 h-14 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {processedVideos.length === 0 ? (
                <div className="text-center py-20 text-gray-600">
                  No matching items found in this section.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {processedVideos.slice(0, visibleCount).map((video, index) => (
                    <VideoCard key={video.id} video={video} index={index} />
                  ))}
                </div>
              )}

              {(processedVideos.length > visibleCount || hasMorePages) && (
                <div className="flex justify-center mt-12">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-8 py-3 rounded-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-800 text-white font-semibold transition-all duration-300 flex items-center gap-2"
                  >
                    {loadingMore ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Loading...
                      </>
                    ) : (
                      "Load More Videos"
                    )}
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </section>
    </>
  );
};

export default GalleryVideos;